import React from 'react';
import { motion } from 'framer-motion';
import { FaTimes } from 'react-icons/fa';

export const CommissionDetailsModal = ({
    isOpen,
    onClose,
    commission,
    campaignName,
    campaignType,
    agreementStatus,
}) => {
    if (!isOpen) return null;

    const getCommissionTypeLabel = (model) => {
        switch (model) {
            case 'fixed':
                return 'Fixed Rate';
            case 'percentage':
                return 'Percentage Based';
            default:
                return 'Unknown';
        }
    };

    const getIndustryLabel = (field) => {
        const labels = {
            amount: 'Fixed Commission',
            percentage: 'Commission Percentage',
            cpe: 'Cost Per Engagement (CPE)',
            cpm: 'Cost Per Thousand Impressions (CPM)',
            model: 'Commission Model',
            currency: 'Currency',
        };
        return labels[field] || field;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-lg shadow-xl w-full max-w-md"
            >
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Commission Details</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <FaTimes size={20} />
                    </button>
                </div>

                <div className="p-6">
                    <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-500 mb-2">Campaign</h4>
                        <p className="text-gray-900 font-medium">{campaignName}</p>
                    </div>

                    <div className="space-y-4">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-600">Campaign Type</span>
                            <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                                campaignType === 'Advertising'
                                    ? 'bg-blue-100 text-blue-800'
                                    : campaignType === 'Affiliate'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-orange-100 text-orange-800'
                            }`}>
                                {campaignType}
                            </span>
                        </div>

                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-600">{getIndustryLabel('model')}</span>
                            <span className="text-sm font-medium text-gray-900">
                                {getCommissionTypeLabel(commission.model)}
                            </span>
                        </div>

                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-600">{getIndustryLabel('currency')}</span>
                            <span className="text-sm font-medium text-gray-900">
                                {commission.currency.code} ({commission.currency.symbol})
                            </span>
                        </div>

                        {commission.model === 'fixed' && commission.amount && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                <span className="text-sm text-gray-600">{getIndustryLabel('amount')}</span>
                                <span className="text-sm font-medium text-gray-900">
                                    {commission.currency.symbol}{commission.amount.toFixed(2)}
                                </span>
                            </div>
                        )}

                        {commission.model === 'percentage' && commission.percentage && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                <span className="text-sm text-gray-600">{getIndustryLabel('percentage')}</span>
                                <span className="text-sm font-medium text-gray-900">
                                    {commission.percentage}%
                                </span>
                            </div>
                        )}

                        {commission.cpe !== null && commission.cpe !== undefined && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                <span className="text-sm text-gray-600">{getIndustryLabel('cpe')}</span>
                                <span className="text-sm font-medium text-gray-900">
                                    {commission.currency.symbol}{commission.cpe.toFixed(2)}
                                </span>
                            </div>
                        )}

                        {commission.cpm !== null && commission.cpm !== undefined && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                <span className="text-sm text-gray-600">{getIndustryLabel('cpm')}</span>
                                <span className="text-sm font-medium text-gray-900">
                                    {commission.currency.symbol}{commission.cpm.toFixed(2)}
                                </span>
                            </div>
                        )}

                        {agreementStatus && (
                            <div className="flex justify-between items-center py-2">
                                <span className="text-sm text-gray-600">Agreement Status</span>
                                <span
                                    className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                        agreementStatus === 'agreed_with_influencer'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-yellow-100 text-yellow-800'
                                    }`}
                                >
                                    {agreementStatus === 'agreed_with_influencer'
                                        ? 'Agreed with influencer'
                                        : 'Not agreed with influencer'}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="mt-6 pt-4 border-t border-gray-200">
                        <button
                            onClick={onClose}
                            className="w-full bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 transition-colors duration-200"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

